import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Stack, Typography, IconButton, Divider, Card } from '@mui/material'
import { ArrowLeftRight, Settings, GitBranch, Check, Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { MODULE_NAME, PERMISSIONS, ROUTES } from '../../constants'
import { clearBranchScopedCache } from '../../hooks/queryClient'
import { PERMISSIONS_UPDATED_EVENT } from '../../hooks/usePermissionEvents'
import { useLanguage } from '../../i18n'
import {
  getUserDetails,
  getSelectedBranch,
  hasPermission,
  isBranchInactiveForUser,
  mergeUserStatusFromDetails,
  setSelectedBranch,
} from '../../utils'
import { fetchAndCacheBranchPermissions } from '../../utils/permissions'
import { AppAvatar, FloatingPanel, SectionHeader } from '../components'

// Build display-only fields (initials, meta) from a raw branch row.
function decorate(branch) {
  if (!branch) return null
  const name = branch.name || branch.branchName || ''
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  const metaParts = [branch.city, branch.region].filter(Boolean)
  return {
    id: branch.id ?? branch.uuid,
    uuid: branch.uuid,
    initials: initials || 'BR',
    name,
    meta: metaParts.join(' · ') || branch.address || '',
    status: branch.status || '',
    raw: branch,
  }
}

// Pull the merged branch list from the `/me` payload so the per-user status
// (Status[].branchId === branch.id) drives both the rendering and the click
// guard.
function loadBranchesFromDetails() {
  const details = getUserDetails() || {}
  return mergeUserStatusFromDetails(details).map(decorate)
}

const branchActions = [
  {
    nameKey: 'Manage branches',
    icon: <Settings size={18} strokeWidth={2} />,
    to: ROUTES.BRANCH_MANAGEMENT,
    // Branch CRUD is gated by the same branch-scoped `view` permission that
    // governs the "Branch Management" sidebar entry — hide the shortcut when
    // the current branch's payload doesn't grant it.
    permission: { module: MODULE_NAME.BRANCH, action: PERMISSIONS.VIEW },
  },
]

export default function BranchSwitcher({ collapsed = false }) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [branches, setBranches] = useState(loadBranchesFromDetails)
  const [activeId, setActiveId] = useState(() => {
    const selected = getSelectedBranch()
    return selected ? selected.uuid || selected.id : null
  })
  const triggerRef = useRef(null)

  // Bumped on every permission change so the gated actions below re-read
  // the cached branch payload (a branch switch or a role edit rewrites it).
  const [permissionsTick, setPermissionsTick] = useState(0)

  // Keep the list + active pick in sync if storage changes (other tabs,
  // fresh /me response from a different page, logout/login, etc).
  useEffect(() => {
    const refresh = () => setBranches(loadBranchesFromDetails())
    window.addEventListener('focus', refresh)
    window.addEventListener('storage', refresh)
    refresh()
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  useEffect(() => {
    const handler = () => setPermissionsTick((n) => n + 1)
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(PERMISSIONS_UPDATED_EVENT, handler)
  }, [])

  // Drop the actions the current branch's payload doesn't grant. An action
  // without a `permission` entry is always shown.
  const visibleActions = useMemo(
    () =>
      branchActions.filter(
        (action) =>
          !action.permission || hasPermission(action.permission.module, action.permission.action)
      ),
    [permissionsTick]
  )

  // Match the selected id against either `id` or `uuid`. `activeId` is
  // derived from the saved branch as `selected.uuid || selected.id`, so it
  // is usually the branch's uuid — but `decorate()` builds each entry with
  // `id: branch.id ?? branch.uuid`, i.e. the numeric id whenever one
  // exists. Comparing only against `b.id` would never match in that case
  // and `active` would silently fall back to the first branch.
  const active = useMemo(
    () => branches.find((b) => b.id === activeId || b.uuid === activeId) || branches[0] || null,
    [branches, activeId]
  )

  const handlePick = async (branch) => {
    // Block the switch when the user is inactive in the picked branch.
    if (isBranchInactiveForUser(branch)) {
      const status = String(branch.status || '').toUpperCase()
      await Swal.fire({
        title: 'Access Denied',
        text: `You are not allowed to access "${branch.name}" because your account is ${status.toLowerCase()} in this branch. Please contact your administrator.`,
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
        background: 'var(--card-bg, #ffffff)',
        color: 'var(--text-primary, #212529)',
      })
      return
    }
    // Fetch the branch-scoped permission payload *before* the branch is
    // persisted — the request carries the picked branch's `branch-id`
    // header. A failed fetch aborts the switch so the app never runs on a
    // branch whose permissions were never loaded.
    const permissionResult = await fetchAndCacheBranchPermissions(branch.raw || branch)
    if (!permissionResult.ok) {
      await Swal.fire({
        title: 'Unable to load permissions',
        text: `Permissions for "${branch.name}" could not be loaded. Please check your connection and try again.`,
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
        background: 'var(--card-bg, #ffffff)',
        color: 'var(--text-primary, #212529)',
      })
      return
    }

    setActiveId(branch.id)
    setSelectedBranch(branch.raw)
    // Clear React Query cache so all list/detail queries refetch with the
    // new `branch-id` header. Without this, mounted pages keep showing the
    // previous branch's data until they remount or the cache goes stale.
    try {
      clearBranchScopedCache()
    } catch {
      // Cache reset is best-effort; failure here should not block the switch.
    }
    setOpen(false)
  }

  return (
    <Box sx={{ position: 'relative', px: 2, py: 1.5 }}>
      <Card
        ref={collapsed ? null : triggerRef}
        className={`p-2 flex gap-2 justify-between items-center cursor-pointer ${
          collapsed ? 'justify-center border-0' : ''
        }`}
        onClick={() => setOpen((o) => !o)}
        sx={{ bgcolor: 'var(--header-bg)' }}
      >
        {collapsed ? (
          <IconButton ref={triggerRef} size="small" aria-label={t('switch branch')}>
            <GitBranch size={18} strokeWidth={2} style={{ color: 'var(--sidebar-text-muted)' }} />
          </IconButton>
        ) : (
          <>
            <div className="flex gap-2 items-center">
              <AppAvatar variant="light">
                <GitBranch size={16} strokeWidth={2} />
              </AppAvatar>
              <Stack spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
                <SectionHeader
                  label={t('Branch')}
                  fontSize={10}
                  marginBottom={0}
                  paddingBottom={4}
                />
                <Typography variant="body2" noWrap fontWeight={600}>
                  {active?.name || 'Select branch'}
                </Typography>
              </Stack>
            </div>

            <IconButton
              size="small"
              sx={{ color: 'var(--sidebar-text-muted)' }}
              aria-label={t('swap branch')}
            >
              <ArrowLeftRight size={16} strokeWidth={2} />
            </IconButton>
          </>
        )}
      </Card>

      <FloatingPanel
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        width={300}
      >
        <div className="px-1">
          <SectionHeader
            label={t('Your Branches')}
            marginBottom={0}
            style={{ padding: '20px 10px 6px' }}
          />
          <Stack className="branch-switcher py-2 px-1">
            {branches.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                No branches available.
              </Typography>
            )}
            {branches.map((b) => {
              const inactive = isBranchInactiveForUser(b)
              // Same id-or-uuid match as `active` above so the highlight
              // stays in sync with the sidebar label.
              const isActive = b.id === activeId || b.uuid === activeId
              return (
                <div
                  key={b.id}
                  onClick={() => handlePick(b)}
                  className={`branch-switcher__item flex justify-between gap-1 items-center p-2 ${
                    isActive ? ' active' : ''
                  }`}
                  style={inactive ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  title={
                    inactive
                      ? `Inactive in this branch (${String(b.status || '').toUpperCase()})`
                      : undefined
                  }
                >
                  <div className="flex gap-2 items-center">
                    <AppAvatar>{b.initials}</AppAvatar>
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body1" noWrap fontWeight={600}>
                        {b.name}
                      </Typography>
                      <Typography variant="caption" noWrap color="text.secondary">
                        {inactive ? `Inactive — ${String(b.status || '').toUpperCase()}` : b.meta}
                      </Typography>
                    </Stack>
                  </div>

                  {inactive ? (
                    <Lock size={16} strokeWidth={2} color="#ef4444" />
                  ) : activeId === b.id ? (
                    <Check size={18} strokeWidth={2} className="branch-switcher__check" />
                  ) : null}
                </div>
              )
            })}
          </Stack>

          {visibleActions.length > 0 && (
            <>
              <Divider />

              <Stack className="py-3 px-1">
                {visibleActions.map((i, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setOpen(false)
                      if (i.to) navigate(i.to)
                    }}
                    className="branch-switcher__item flex gap-2 p-2 items-center cursor-pointer"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {i.icon}
                    <Typography color="text.disabled" variant="body1" fontWeight={600}>
                      {t(i.nameKey)}
                    </Typography>
                  </div>
                ))}
              </Stack>
            </>
          )}
        </div>
      </FloatingPanel>
    </Box>
  )
}
